import React, { useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import BookingQuoteCalendar from "containers/ListingDetailPage/listing-stay-detail/BookingQuoteCalendar";
import DatesRangeInputWQuote from "containers/ListingDetailPage/listing-stay-detail/DatesRangeInputWQuote";
import BookingQuoteCalendarWMinDays from "containers/ListingDetailPage/listing-stay-detail/BookingQuoteCalendarWMinDays";
import GuestsInput from "containers/ListingDetailPage/listing-stay-detail/GuestsInput";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonCircle from "shared/Button/ButtonCircle";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useFetchQuote } from "hooks/useFetchQuote";
import { useBooking } from "context/BookingContext";
import { useClient } from "ClientContext";
import { useListingBooking } from "context/availabilitiesContext";
import getSymbolFromCurrency from "currency-symbol-map";
import { useTranslation } from "react-i18next";

interface MobileFriendlyBookingProps {
  listingPrices?: any;
  listingId?: string;
  isFooterQuote?: boolean;
  dates?: string[];

  //used for WIX Page integration
  externalReq?: {
    INTERNAL_ID: string;
    LISTING_ID: string;
    INTEGRATION_TYPE: string;
    ACTION: string;
  };
}



const MobileFriendlyBooking: React.FC<MobileFriendlyBookingProps> = ({ dates }) => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState<string>("");
  const [couponError, setCouponError] = useState("");
  const [showCouponBox, setShowCouponBox] = useState(false);

  const { internal_ID, integration_TYPE, global_language, clientName } = useClient();
  const { availabilitiesState } = useListingBooking();
  const effectiveDates = dates || availabilitiesState.availabilities || [];
  const { bookingState } = useBooking();
  const { listingId, startingPrice,roomId } = bookingState;
  const {
    quote,
    isQuote,
    loading,
    quoteError,
    handleFetchQuote,
    couponErrorState,
    setCouponErrorState,
    setIsQuote,
    setError: setQuoteError, // Add this if available, or create a way to clear quote error
    default_currency,
    checkin,
    checkout,
    guestsNum,
  } = useFetchQuote();

  const [isFeesOpen, setIsFeesOpen] = useState(false);
  const [isTaxeOpen, setIsTaxeOpen] = useState(false);
  const [isPromoOpen, setIsPromoOpen] = useState(false);

  const toggleFeesAccordion = () => setIsFeesOpen((prev) => !prev);
  const toggleTaxesAccordion = () => setIsTaxeOpen((prev) => !prev);
  const togglePromosAccordion = () => setIsPromoOpen((prev) => !prev);

  // Enhanced function to handle date changes
  const handleDatesChange = () => {
    setIsQuote(false);
    // Clear quote error when dates change so Search button can appear
    if (setQuoteError) {
      setQuoteError("");
    }
    // Also clear coupon related errors
    setCouponError("");
    setCouponErrorState("");
  };

  function decodeHtml(html: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.documentElement.textContent;
  }

  const currencySign = getSymbolFromCurrency(default_currency);

  // Generate URL for Lodgify checkout
  const lodgifyUrl = useMemo(() => {
    if (!quote || !checkin || !checkout ||   (internal_ID !== "f9ae756d-be1b-4593-87aa-c245416e4ae7" &&
   internal_ID !== "42ac6777-9551-4310-a58f-445e45327656")) return "";

    return `https://checkout.lodgify.com/${global_language}/${clientName}/${listingId}/addons` +
      `?currency=${default_currency}` +
      `&ref=bnbox` +
      `&arrival=${checkin}` +
      `&departure=${checkout}` +
      `&guests=${guestsNum}` +
      (couponCode ? `&coupon=${couponCode}` : "");
  }, [quote, checkin, checkout, internal_ID, global_language, clientName, listingId, default_currency, guestsNum, couponCode]);

  // Generate regular checkout path
  const regularPath = useMemo(() => {
    if (!quote || !checkin || !checkout || internal_ID === "f9ae756d-be1b-4593-87aa-c245416e4ae7") return "";

    return `/checkout/${listingId}/${default_currency}/${checkin}/${checkout}/${guestsNum}/${internal_ID}/${couponCode}`;
  }, [listingId, default_currency, checkin, checkout, quote, guestsNum, internal_ID, couponCode]);

  // Handle book now click
  const handleBookNow = ({ accessKey }: { accessKey: string }) => {
    console.log("handleBookNow called with accessKey:", lodgifyUrl);
    if (internal_ID === accessKey && lodgifyUrl) {
      window.open(lodgifyUrl, "_blank", "noopener,noreferrer");
    }
    // For regular path, the ButtonPrimary component will handle the navigation via href
  };

  const applyCoupon = () => {
    if (!couponCode) {
      setCouponError(t("Please enter a coupon code."));
      return;
    }
    handleFetchQuote({ couponCode, listingId,roomId:roomId });
    setCouponError("");
    setCouponErrorState("");
  };

  const searchWithoutCoupon = () => {
    handleFetchQuote({ listingId,roomId:roomId });
    setCouponCode("");
    setCouponError("");
    setCouponErrorState("");
  };

  return (
    <div
      className="listingSectionSidebar__wrap space-y-4 w-full max-w-md min-w-0 !shadow-none !border-0 !bg-transparent"
    >
      {/* Header Section */}
      <div className="flex justify-between">
        <span className="text-3xl font-semibold">
          <NumericFormat
            value={quote?.preTotal || startingPrice}
            displayType="text"
            thousandSeparator
            prefix={currencySign}
            decimalScale={0}
          />
          <span className="ml-1 text-base font-normal text-neutral-500 dark:text-neutral-400">
            /{default_currency}
          </span>
        </span>
        <span className="ml-1 text-base font-normal text-neutral-500 dark:text-neutral-400">
          {isQuote ? t("Total Price") : t("Starting From")}
        </span>
      </div>

      {/* Form Section */}
      <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-3xl">
        {
          internal_ID === "placeholder" ? (
            <BookingQuoteCalendarWMinDays
              className="flex-1 z-[11]"
              onDatesChange={handleDatesChange}
              dates={effectiveDates}
            />
          ) : internal_ID !== "60ec32ed-8a2c-4a9b-aae4-799793a2e237" ? ( //use on All clients except Chaletduplateau
            <BookingQuoteCalendar
              className="flex-1 z-[11]"
              onDatesChange={handleDatesChange}
              dates={effectiveDates}
            />
          ) : (
            <DatesRangeInputWQuote  // use for Chaletduplateau client only
              className="flex-1 z-[11]"
              onDatesChange={handleDatesChange}
              dates={effectiveDates}
            />
          )
        }
        <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
        <GuestsInput className="flex-1" />
      </form>

      <div className="text-red-500 text-sm">{quoteError !== "The minimum stay for this rental is 30 days" && t(quoteError ?? '')}</div>

      {isQuote && (
        <div className="space-y-4">
          {/* Nightly Price Section */}
          <div className="accordion-header w-full text-left py-2 px-0 flex justify-between items-center">
            <div className="flex items-center">
              <NumericFormat
                value={quote?.nightlyPrice}
                displayType="text"
                thousandSeparator
                prefix={currencySign}
                decimalScale={0}
                className="mr-1"
              />
              <span className="mr-1">
                X {quote?.lengthOfStay} {t("nights")}
              </span>
            </div>
            <NumericFormat
              value={quote?.nightlyTotal}
              displayType="text"
              thousandSeparator
              prefix={currencySign}
              decimalScale={0}
            />
          </div>

          {/* PROMO Section */}
          {(quote?.promoItems?.length) ? (
            <div className="space-y-2">
              <button
                className="accordion-header w-full text-left text-green-600 py-2 px-0 flex justify-between items-center"
                onClick={togglePromosAccordion}
              >
                <span>Promotions</span>
                {quote?.totalPromo ? (
                  <NumericFormat
                    value={quote.totalPromo}
                    displayType="text"
                    thousandSeparator
                    prefix={quote.promoItems[0]?.type === "PERCENT" ? "" : currencySign}
                    suffix={quote.promoItems[0]?.type === "PERCENT" ? "%" : ""}
                    decimalScale={0}
                  />
                ) : null}
              </button>
              {isPromoOpen && (
                <div className="accordion-content px-4 py-2 space-y-2">
                  {quote?.promoItems?.map((promotion, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{decodeHtml((promotion as any).name || (promotion as any)?.title || '')}</span>
                      <NumericFormat
                        value={promotion.value || (promotion as any).amount}
                        displayType="text"
                        thousandSeparator
                        prefix={currencySign}
                        decimalScale={0}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Fees Section */}
          <div className="space-y-2">
            <button
              className="accordion-header w-full text-left py-2 px-0 flex justify-between items-center"
              onClick={toggleFeesAccordion}
            >
              <span>{t("Fees")}</span>
              <NumericFormat
                value={quote?.totalFees || 0}
                displayType="text"
                thousandSeparator
                prefix={currencySign}
                decimalScale={0}
              />
            </button>
            {isFeesOpen && (
              <div className="accordion-content px-4 py-2 space-y-2">
                {quote?.feesItems?.length ? (
                  quote.feesItems.map((fee, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{fee.title}</span>
                      <NumericFormat
                        value={fee.amount}
                        displayType="text"
                        thousandSeparator
                        prefix={currencySign}
                        decimalScale={0}
                      />
                    </div>
                  ))
                ) : (
                  <div className="text-neutral-500">
                    {t("No additional fees")}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Coupon Section */}
          {(integration_TYPE === "guesty" || integration_TYPE === "hostaway") && (
            <>
              <div className="border-b border-neutral-200 dark:border-neutral-700 my-4"></div>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showCouponBox}
                    onChange={() => {
                      setShowCouponBox((prev) => !prev);
                      setCouponCode("");
                      setCouponError("");
                      setCouponErrorState("");
                    }}
                    className="h-4 w-4 border border-primary-500 rounded text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    I have a coupon code
                  </span>
                </label>
                {showCouponBox && (
                  <div className="flex items-center">
                    <input
                      type="text"
                      className="flex-1 px-4 py-2 border border-neutral-300 rounded-md text-sm dark:bg-neutral-800 dark:text-neutral-300 focus:outline-none focus:ring-0 focus:border-transparent"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponError("");
                        setCouponErrorState("");
                      }}
                    />
                    <ButtonPrimary
                      onClick={applyCoupon}
                      className="ml-2 h-12 min-h-12"
                    >
                      Apply
                    </ButtonPrimary>
                  </div>
                )}
                {(couponError || couponErrorState) && (
                  <div className="text-red-500 text-sm mt-2">
                    {couponErrorState || couponError}
                  </div>
                )}
              </div>
              <div className="border-b border-neutral-200 dark:border-neutral-700 my-4"></div>
            </>
          )}

          {/* Subtotal Before Taxes */}
          <div className="flex justify-between font-semibold">
            <span>{t("Subtotal before taxes")}</span>
            <NumericFormat
              value={quote?.subTotal || 0}
              displayType="text"
              thousandSeparator
              prefix={currencySign}
              decimalScale={0}
            />
          </div>

          {/* Taxes Section */}
          <div className="space-y-2">
            <button
              className="accordion-header w-full text-left py-2 px-0 flex justify-between items-center"
              onClick={toggleTaxesAccordion}
            >
              <span>Taxes</span>
              {quote?.totalTaxes ? (
                <NumericFormat
                  value={quote?.totalTaxes}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySign}
                  decimalScale={0}
                />
              ) : (
                <span>{t("Taxes included")}</span>
              )}
            </button>
            {isTaxeOpen && (
              <div className="accordion-content px-4 py-2 space-y-2">
                {quote?.taxesItems?.length
                  ? quote.taxesItems.map((tax, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>{tax.title}</span>
                      <NumericFormat
                        value={tax.amount}
                        displayType="text"
                        thousandSeparator
                        prefix={currencySign}
                        decimalScale={0}
                      />
                    </div>
                  ))
                  : null}
              </div>
            )}
          </div>
          <div className="border-b border-neutral-200 dark:border-neutral-700 my-4"></div>
          {/* Total */}
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <NumericFormat
              value={quote?.stayTotal || 0}
              displayType="text"
              thousandSeparator
              prefix={currencySign}
              decimalScale={0}
            />
          </div>
        </div>
      )}

      {/* Footer Buttons */}
      {loading ? (
        <ButtonPrimary loading={loading} disabled>
          {t("Searching...")}
        </ButtonPrimary>
      ) : isQuote ? (
        <div className="flex items-center space-x-2">
          <ButtonCircle
            onClick={() => setIsQuote(false)}
            className="min-w-12 min-h-12"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </ButtonCircle>

          <ButtonPrimary
            buttonId="quote-booknow-btn"
            href={internal_ID === "f9ae756d-be1b-4593-87aa-c245416e4ae7" || internal_ID === "42ac6777-9551-4310-a58f-445e45327656" ? undefined : regularPath}
            onClick={
              internal_ID === "f9ae756d-be1b-4593-87aa-c245416e4ae7" || internal_ID === "42ac6777-9551-4310-a58f-445e45327656"
                ? () => handleBookNow({ accessKey: internal_ID })
                : undefined
            }
            className="flex-1 h-12 min-h-12 disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 hover:text-neutral-10"
          >
            {t("Book Now")}
          </ButtonPrimary>
        </div>
      ) : (
        <>
          {internal_ID === "f9ae756d-be1b-4593-87aa-c245416e4ae7" &&
            (<p id="kahala" className="text-sm italic text-neutral-600 leading-relaxed break-words" >The property will be made exclusively available to the renter for a full period of 30-days and no one else may occupy the property during that period. Rates based on a 30 day rental period. Please Inquire.</p>
            )}        <ButtonPrimary
              buttonId="quote-search-btn"
              className="disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 hover:text-neutral-10"
              onClick={
                quoteError !== "The minimum stay for this rental is 30 days"
                  ? searchWithoutCoupon
                  : () => window.open(`https://www.thekahalamansion.com/bookinginquiry?checkin=${checkin}&checkout=${checkout}`, "_blank", "noopener,noreferrer")
              }
            >
            {quoteError === "The minimum stay for this rental is 30 days" ? "Booking Inquiry" : t("Search")}
          </ButtonPrimary>
        </>
      )}
    </div>
  );
};

export default MobileFriendlyBooking;
